import { ChangeDetectionStrategy, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { AuthService, GeneratorService } from '@psnb/services';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { BaseResponseDTO, GeneratorSearchDTO, RecoverRequest, RefuelRequest } from '@psnb/psnb-api';
import { Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { PowerSupplyMessageDialog } from '../../../common/message-dialog/power-supply-message-dialog';
import { RefuelRecoverAction } from '../telemetry-generator.model';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'psnb-refuel-recover-generator-dialog',
  templateUrl: './refuel-recovery-generator-dialog.component.html'
})

export class RefuelRecoveryGeneratorDialogComponent implements OnInit, OnDestroy {
  private readonly destroyed$: Subject<void>;

  private generatorDataResponse: GeneratorSearchDTO;
  private generatorId: string;
  private messageDialogRef: MatDialogRef<PowerSupplyMessageDialog> | undefined;
  private timeValue: string = new Date().toISOString();
  private userName: string;

  public constructor(
    private authService: AuthService,
    private dialog: MatDialog,
    private refuelRecoveryGeneratorDialogRef: MatDialogRef<RefuelRecoveryGeneratorDialogComponent>,
    private generatorService: GeneratorService,
    @Inject(MAT_DIALOG_DATA) public recoverRefuelData: { action: RefuelRecoverAction; generatorId: string }
  ) {
    this.destroyed$ = new Subject<void>();
  }

  public ngOnInit(): void {
    this.generatorId = this.recoverRefuelData.generatorId;
    this.userName = `${this.authService.getUserDetails.firstName} ${this.authService.getUserDetails.lastName}`;
  }

  public ngOnDestroy(): void {
    this.messageDialogRef?.close();
    this.destroyed$?.next();
    this.destroyed$?.complete();
  }

  public onCancel(): void {
    this.refuelRecoveryGeneratorDialogRef.close();
  }

  public async refuelRecoverConfirm(userAction: RefuelRecoverAction): Promise<void> {
    await this.fetchGeneratorDataById(this.generatorId);
    if (userAction === RefuelRecoverAction.Refuel) {
      const refuelRequest: RefuelRequest = {
        generatorId: this.generatorId,
        lastModified: this.timeValue,
        lastModifiedBy: this.userName
      };
      this.generatorService.refuelGenerator(refuelRequest)
        .pipe(takeUntil(this.destroyed$))
        .subscribe((refuelResponse: BaseResponseDTO): void => {
          if (refuelResponse.success) {
            this.messageDialogRef?.close();
            this.messageDialogRef = this.dialog.open(PowerSupplyMessageDialog, {
              data: { message: 'Refuel successful' }
            });
            this.messageDialogRef.afterClosed().pipe(take(1)).subscribe((): void => {
              this.refuelRecoveryGeneratorDialogRef.close(true);
            });
          } else {
            this.messageDialogRef?.close();
            this.messageDialogRef = this.dialog.open(PowerSupplyMessageDialog, {
              data: { message: 'Refuel failed' }
            });
            this.messageDialogRef.afterClosed().pipe(take(1)).subscribe((): void => {
              this.refuelRecoveryGeneratorDialogRef.close(false);
            });
          }
        }, (): void => {
          this.messageDialogRef?.close();
          this.messageDialogRef = this.dialog.open(PowerSupplyMessageDialog, {
            data: { message: 'Refuel failed' }
          });
          this.messageDialogRef.afterClosed().pipe(take(1)).subscribe((): void => {
            this.refuelRecoveryGeneratorDialogRef.close(false);
          });
        });
    } else if (userAction === RefuelRecoverAction.Recover) {
      const recoverRequest: RecoverRequest = {
        address: this.generatorDataResponse?.address,
        city: this.generatorDataResponse?.city,
        comments: this.generatorDataResponse?.comments,
        generatorId: this.generatorId,
        lastModified: this.timeValue,
        lastModifiedBy: this.userName,
        latitude: this.generatorDataResponse?.latitude,
        longitude: this.generatorDataResponse?.longitude,
        state: this.generatorDataResponse?.state,
        zipCode: this.generatorDataResponse?.zipcode
      };
      this.generatorService.recoverGenerator(recoverRequest)
        .pipe(takeUntil(this.destroyed$))
        .subscribe((recoverResponse: BaseResponseDTO): void => {
          if (recoverResponse.success) {
            this.messageDialogRef?.close();
            this.messageDialogRef = this.dialog.open(PowerSupplyMessageDialog, {
              data: { message: 'Recover successful' }
            });
            this.messageDialogRef.afterClosed().pipe(take(1)).subscribe((): void => {
              this.refuelRecoveryGeneratorDialogRef.close(true);
            });
          } else {
            this.messageDialogRef?.close();
            this.messageDialogRef = this.dialog.open(PowerSupplyMessageDialog, {
              data: { message: 'Recover failed' }
            });
            this.messageDialogRef.afterClosed().pipe(take(1)).subscribe((): void => {
              this.refuelRecoveryGeneratorDialogRef.close(false);
            });
          }
        }, (): void => {
          this.messageDialogRef?.close();
          this.messageDialogRef = this.dialog.open(PowerSupplyMessageDialog, {
            data: { message: 'Recover failed' }
          });
          this.messageDialogRef.afterClosed().pipe(take(1)).subscribe((): void => {
            this.refuelRecoveryGeneratorDialogRef.close(false);
          });
        });
    }
  }

  private fetchGeneratorDataById(generatorId: string): Promise<void> {
    return new Promise<void>((resolve: () => void, reject: (reason?: GeneratorSearchDTO[]) => void): void => {
      this.generatorService.generatorSearchByAssetId(generatorId)
        .pipe(takeUntil(this.destroyed$))
        .subscribe(
          (response: GeneratorSearchDTO[]): void => {
            this.generatorDataResponse = response[0];
            resolve();
          },
          (errorResponse: GeneratorSearchDTO[]): void => {
            this.messageDialogRef?.close();
            this.messageDialogRef = this.dialog.open(PowerSupplyMessageDialog, {
              data: { message: 'Error retrieving generator data' }
            });
            reject(errorResponse);
          }
        );
    });
  }
}
